import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiFilm, FiVideo, FiUpload, FiDownload, FiRefreshCw, FiLoader } from 'react-icons/fi';
import { BsFilePlay } from 'react-icons/bs';
import SEO from '../components/SEO';
import { videoCompressorSchemas } from '../schemas';
import API_BASE_URL from '../config';
import './VideoCompressor.css';

const VideoCompressor = () => {
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const outputVideoRef = useRef(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [outputVideoUrl, setOutputVideoUrl] = useState(null);
  const [outputVideoName, setOutputVideoName] = useState('');
  const [originalSize, setOriginalSize] = useState(0);
  const [compressedSize, setCompressedSize] = useState(0);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressProgress, setCompressProgress] = useState(0);
  const [error, setError] = useState('');
  const [compressionMode, setCompressionMode] = useState('quality'); // quality or size
  const [quality, setQuality] = useState('medium'); // low, medium, high
  const [targetSize, setTargetSize] = useState(10);
  const [sizeUnit, setSizeUnit] = useState('MB'); // MB or KB

  useEffect(() => {
    return () => {
      if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    };
  }, [videoPreviewUrl]);

  useEffect(() => {
    return () => {
      if (outputVideoUrl) URL.revokeObjectURL(outputVideoUrl);
    };
  }, [outputVideoUrl]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      validateAndSetVideo(file);
    }
  };

  const validateAndSetVideo = (file) => {
    if (!file.type.startsWith('video/')) {
      setError('Please upload a valid video file');
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      setError('Video file size should be less than 100MB');
      return;
    }

    setError('');
    const url = URL.createObjectURL(file);
    setVideoPreviewUrl(url);
    setVideoFile(file);
    setOriginalSize(file.size);
    setOutputVideoUrl(null);
    setOutputVideoName('');
    setCompressedSize(0);
    setCompressProgress(0);

    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = url;
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      validateAndSetVideo(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const compressVideo = async () => {
    if (!videoFile) return;

    // Validate target size if in size mode
    if (compressionMode === 'size') {
      const targetSizeInMB = sizeUnit === 'KB' ? targetSize / 1024 : targetSize;
      const originalSizeInMB = originalSize / (1024 * 1024);
      
      if (targetSizeInMB <= 0) {
        setError('Please enter a valid target size');
        return;
      }
      
      if (targetSizeInMB >= originalSizeInMB) {
        setError('Target size must be smaller than original file size');
        return;
      }
    }

    let progressInterval = null;
    setIsCompressing(true);
    setCompressProgress(0);
    setError('');
    setOutputVideoName('');
    setCompressedSize(0);

    if (outputVideoUrl) {
      URL.revokeObjectURL(outputVideoUrl);
    }
    setOutputVideoUrl(null);

    try {
      const formData = new FormData();
      formData.append('video', videoFile);
      formData.append('mode', compressionMode);
      
      if (compressionMode === 'quality') {
        formData.append('quality', quality);
      } else {
        formData.append('targetSize', targetSize);
        formData.append('sizeUnit', sizeUnit);
      }

      progressInterval = setInterval(() => {
        setCompressProgress((previous) => Math.min(previous + 5, 90));
      }, 300);

      const response = await fetch(`${API_BASE_URL}/api/compress-video`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Compression failed');
      }

      const videoBytes = Uint8Array.from(atob(data.video), (character) => character.charCodeAt(0));
      const videoBlob = new Blob([videoBytes], { type: 'video/mp4' });

      setOutputVideoUrl(URL.createObjectURL(videoBlob));
      setOutputVideoName(data.filename);
      setCompressedSize(data.compressedSize || videoBlob.size);
      setCompressProgress(100);
    } catch (err) {
      console.error('Video compression error:', err);
      setError(err.message || 'Failed to compress video. Please try again.');
    } finally {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      setIsCompressing(false);
    }
  };

  const downloadOutputVideo = () => {
    if (!outputVideoUrl) return;

    const link = document.createElement('a');
    link.href = outputVideoUrl;
    const videoName = videoFile ? videoFile.name.replace(/\.[^/.]+$/, '') : 'video';
    link.download = `${videoName}-compressed.mp4`;
    link.click();
  };

  const resetCompressor = () => {
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    if (outputVideoUrl) URL.revokeObjectURL(outputVideoUrl);

    setVideoFile(null);
    setVideoPreviewUrl(null);
    setOutputVideoUrl(null);
    setOutputVideoName('');
    setOriginalSize(0);
    setCompressedSize(0);
    setError('');
    setCompressProgress(0);
    setCompressionMode('quality');
    setQuality('medium');
    setTargetSize(10);
    setSizeUnit('MB');

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (videoRef.current) {
      videoRef.current.pause();
    }
    if (outputVideoRef.current) {
      outputVideoRef.current.pause();
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const index = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, index)).toFixed(2)) + ' ' + sizes[index];
  };

  const compressionRatio = originalSize && compressedSize 
    ? ((originalSize - compressedSize) / originalSize * 100).toFixed(1)
    : 0;

  return (
    <div className="video-compressor-page">
      <SEO 
        title="Video Compressor Online – Reduce Video Size Free"
        description="Compress large video files online without losing quality. Reduce MP4 video size for upload, email or sharing — free & fast."
        keywords="video compressor, reduce video size, compress mp4 online"
        path="/video-compressor"
        schema={videoCompressorSchemas}
      />
      <section className="tool-hero">
        <div className="tool-hero-container">
          <motion.div
            className="tool-icon-large"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <BsFilePlay />
          </motion.div>

          <motion.h1
            className="tool-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Video Compressor
          </motion.h1>

          <motion.p
            className="tool-description"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Reduce video file size while maintaining quality. Upload videos up to 100MB.
          </motion.p>
        </div>
      </section>

      <section className="tool-workspace">
        <div className="workspace-container">
          <AnimatePresence mode="wait">
            {!videoFile ? (
              <motion.div
                key="upload"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="upload-zone"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
              >
                <motion.div
                  className="upload-icon"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <FiUpload />
                </motion.div>
                <h3>Upload Your Video</h3>
                <p>Drag and drop or click to browse (up to 100MB)</p>
                <p className="supported-formats">Supported: MP4, MOV, AVI, WebM, MKV</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </motion.div>
            ) : (
              <motion.div
                key="compressor"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="compressor-workspace"
              >
                <div className="video-grid">
                  <motion.div
                    className="preview-card-minimal"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="card-header-small">
                      <FiFilm />
                      <span>Original Video</span>
                    </div>
                    <div className="card-content">
                      <video
                        key={videoPreviewUrl}
                        ref={videoRef}
                        src={videoPreviewUrl}
                        controls
                        playsInline
                        preload="auto"
                        className="video-preview"
                        onLoadedData={() => console.log('Video ready to play')}
                        onError={() => {
                          setError('Failed to load video. Please try another file.');
                        }}
                      />
                    </div>
                    <div className="video-info-bar">
                      <span className="video-info-name">{videoFile ? videoFile.name : ''}</span>
                      <span className="video-info-size">{formatFileSize(originalSize)}</span>
                    </div>
                  </motion.div>

                  <motion.div
                    className="preview-card-minimal"
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <div className="card-header-small">
                      <FiVideo />
                      <span>Compressed Output</span>
                    </div>
                    <div className="card-content">
                      <AnimatePresence mode="wait">
                        {isCompressing ? (
                          <motion.div
                            key="compressing"
                            className="loading-circle"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            <FiLoader className="spinning" />
                            <span>Compressing... {compressProgress}%</span>
                          </motion.div>
                        ) : outputVideoUrl ? (
                          <motion.div
                            key="output-video"
                            className="video-result-minimal"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                          >
                            <video
                              key={outputVideoUrl}
                              ref={outputVideoRef}
                              src={outputVideoUrl}
                              controls
                              playsInline
                              preload="auto"
                              className="video-preview"
                            />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="waiting"
                            className="waiting-minimal"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                          >
                            <BsFilePlay />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    <div className="video-info-bar">
                      <span className="video-info-name">{outputVideoName || 'Waiting for output'}</span>
                      <span className="video-info-size">
                        {compressedSize ? (
                          <>
                            {formatFileSize(compressedSize)}
                            {compressionRatio > 0 && (
                              <span className="compression-savings"> (-{compressionRatio}%)</span>
                            )}
                          </>
                        ) : ''}
                      </span>
                    </div>
                  </motion.div>
                </div>

                {!outputVideoUrl && !isCompressing && (
                  <motion.div
                    className="compression-controls"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <div className="compression-mode-tabs">
                      <button
                        className={`mode-tab ${compressionMode === 'quality' ? 'active' : ''}`}
                        onClick={() => setCompressionMode('quality')}
                      >
                        Quality Preset
                      </button>
                      <button
                        className={`mode-tab ${compressionMode === 'size' ? 'active' : ''}`}
                        onClick={() => setCompressionMode('size')}
                      >
                        Target File Size
                      </button>
                    </div>

                    {compressionMode === 'quality' ? (
                      <div className="quality-mode">
                        <h3>Select Compression Quality</h3>
                        <div className="quality-options">
                          <button
                            className={`quality-btn ${quality === 'high' ? 'active' : ''}`}
                            onClick={() => setQuality('high')}
                          >
                            <span className="quality-label">High Quality</span>
                            <span className="quality-desc">Best quality, larger file</span>
                          </button>
                          <button
                            className={`quality-btn ${quality === 'medium' ? 'active' : ''}`}
                            onClick={() => setQuality('medium')}
                          >
                            <span className="quality-label">Medium Quality</span>
                            <span className="quality-desc">Balanced quality & size</span>
                          </button>
                          <button
                            className={`quality-btn ${quality === 'low' ? 'active' : ''}`}
                            onClick={() => setQuality('low')}
                          >
                            <span className="quality-label">Low Quality</span>
                            <span className="quality-desc">Smallest file size</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="size-mode">
                        <h3>Enter Target File Size</h3>
                        <div className="size-input-group">
                          <label htmlFor="target-size">Maximum output size</label>
                          <div className="input-with-unit">
                            <input
                              id="target-size"
                              type="number"
                              min="0.1"
                              step="0.1"
                              value={targetSize}
                              onChange={(e) => setTargetSize(e.target.value)}
                              className="size-input"
                            />
                            <select
                              value={sizeUnit}
                              onChange={(e) => setSizeUnit(e.target.value)}
                              className="unit-select"
                            >
                              <option value="MB">MB</option>
                              <option value="KB">KB</option>
                            </select>
                          </div>
                          <p className="size-hint">
                            Original size: {formatFileSize(originalSize)}
                          </p>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {error && (
                  <motion.div
                    className="error-message"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {error}
                  </motion.div>
                )}

                <motion.div
                  className="action-buttons-row"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  {!outputVideoUrl ? (
                    <motion.button
                      className="btn-primary-large"
                      onClick={compressVideo}
                      disabled={isCompressing}
                      whileHover={{ scale: isCompressing ? 1 : 1.05 }}
                      whileTap={{ scale: isCompressing ? 1 : 0.95 }}
                    >
                      {isCompressing ? (
                        <>
                          <FiLoader className="spinning" />
                          Compressing... {compressProgress}%
                        </>
                      ) : (
                        <>
                          <BsFilePlay />
                          Compress Video
                        </>
                      )}
                    </motion.button>
                  ) : (
                    <>
                      <motion.button
                        className="btn-primary-large"
                        onClick={downloadOutputVideo}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring' }}
                      >
                        <FiDownload />
                        Download Compressed Video
                      </motion.button>
                      <motion.button
                        className="btn-secondary"
                        onClick={resetCompressor}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <FiRefreshCw />
                        Start Over
                      </motion.button>
                    </>
                  )}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      <section className="info-section">
        <div className="info-container">
          <motion.div
            className="info-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3>Why Compress Videos?</h3>
            <ul>
              <li>Reduce file size for faster uploads and downloads</li>
              <li>Save storage space on your devices</li>
              <li>Share videos easily via email and messaging apps</li>
              <li>Optimize videos for web and social media platforms</li>
              <li>Maintain good quality with efficient compression</li>
            </ul>
          </motion.div>

          <motion.div
            className="info-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h3>Compression Quality Guide</h3>
            <ul>
              <li><strong>High Quality:</strong> Best for archiving and professional use</li>
              <li><strong>Medium Quality:</strong> Perfect balance for most use cases</li>
              <li><strong>Low Quality:</strong> Maximum compression for sharing</li>
              <li>H.264 codec for universal compatibility</li>
              <li>Fast server-side processing with FFmpeg</li>
            </ul>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default VideoCompressor;
